import { z } from "zod";
import { DiceRollConfigSchema } from "@/lib/core/schemas/dice";
import { LogicInspectorSchema } from "@/lib/kernel/evaluators";
import { validatePredicateForSite } from "@/lib/kernel/predicates/validate";
import type { RemovalTrigger } from "./types";

/**
 * LAF-166 — Zod validation for `RemovalTrigger<R>`.
 *
 * The schema validates SHAPE only. The `when?: Predicate<R>` field is
 * intentionally typed as `z.unknown()` here; predicate-shape validation
 * is delegated to the LAF-163 substrate via
 * `validatePredicateForSite(pred, "removalTriggerWhen")` from
 * `lib/kernel/predicates/validate.ts`. `validateRemovalTrigger` below
 * combines both — Zod for the trigger envelope plus LAF-163 for the
 * (optional) gating predicate.
 *
 * Closed-kind set per spec §4.1. Retired Q6 values are explicitly
 * excluded:
 *   - `tickOn: 'turn-start'` (use `'turn-changed'` instead),
 *   - `rest-triggered.effect: 'decrement-or-remove'` (use `'decompose'`;
 *     exhaustion-shaped decrement semantics live in `ApplyMutatorSpec`,
 *     shipping with LAF-94).
 *
 * Called at profile-register-time (via `profile-predicates/registry.ts`)
 * and at apply-time when `apply_condition` resolves caller overrides
 * into the final triggers array.
 */
const RefExprSchema = z.object({
  ref: z.string(),
});

// `durationTurns` accepts a literal or a `RollModifierConfigTemplate<R>`-
// shaped ref. Shape-level validation only; the substrate types narrow at
// the call site.
const NumberOrRefExpr = z.union([z.number(), RefExprSchema]);

const TickDownSchema = z.object({
  kind: z.literal("tick-down"),
  durationTurns: NumberOrRefExpr,
  tickOn: z.union([z.literal("turn-changed"), z.literal("turn-ended")]),
  when: z.unknown().optional(),
});

const ResolutionSchema = z.object({
  diceConfig: DiceRollConfigSchema,
  inspectors: z.array(LogicInspectorSchema),
  formula: z.string().optional(),
});

const RollGatedSchema = z.object({
  kind: z.literal("roll-gated"),
  triggerEvent: z.string(),
  resolution: ResolutionSchema,
  removeOnOutcomes: z.array(z.string()),
  when: z.unknown().optional(),
  // LAF-280 — required save ability the roll-gated save uses (ruleset-neutral
  // string; the ruleset's save-type-mapping resolves it to an AbilityScore).
  saveType: z.string(),
  // LAF-245 — threshold-derivation registry fn id (eager-bake). Optional;
  // absent → static attrs.dc threshold (poisoned). A non-string value is
  // rejected by z.string() itself; no .strict() needed (and the sibling
  // RemovalTrigger schemas are non-strict too).
  thresholdFromEvent: z.string().optional(),
  // LAF-242 — the SaveRollCallback kind the worker forges. Default
  // 'remove-condition' (poisoned, unchanged). Concentration sets
  // 'break-concentration' to cascade-teardown the anchor + every hexed target.
  onRemove: z
    .union([z.literal("remove-condition"), z.literal("break-concentration")])
    .optional(),
  // LAF-242 — event-scoped gate (damage-vs-heal classification). Reads the
  // triggering event's resourceId + signed amount, which the entity-only
  // `when?` context cannot see.
  whenEvent: z
    .object({
      resourceId: z.string().optional(),
      direction: z
        .union([z.literal("decrease"), z.literal("increase")])
        .optional(),
    })
    .optional(),
});

const RestTriggeredSchema = z.object({
  kind: z.literal("rest-triggered"),
  restKind: z.string(),
  effect: z.literal("decompose"),
  when: z.unknown().optional(),
});

const JsonLiteralSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonLiteralSchema),
    z.record(JsonLiteralSchema),
  ])
);

const ActionResolvedEffectSchema = z
  .discriminatedUnion("kind", [
    z.object({ kind: z.literal("decompose") }),
    z.object({
      kind: z.literal("refresh-attrs"),
      attrs: z.record(JsonLiteralSchema),
    }),
    z.object({
      kind: z.literal("refresh-duration"),
      rounds: z.number().int().nonnegative(),
    }),
  ])
  .optional();

const ActionResolvedSchema = z.object({
  kind: z.literal("action-resolved"),
  actionRef: z.string().optional(),
  actionClasses: z.array(z.string().min(1)).min(1).optional(),
  forcesSave: z.literal(true).optional(),
  effect: ActionResolvedEffectSchema,
  when: z.unknown().optional(),
});

const DamageTakenSchema = z.object({
  kind: z.literal("damage-taken"),
  when: z.unknown().optional(),
});

const PendingActionResolvedSchema = z.object({
  kind: z.literal("pending-action-resolved"),
  pendingActionId: z.string().min(1),
  when: z.unknown().optional(),
});

const SourceTurnStartedSchema = z.object({
  kind: z.literal("source-turn-started"),
  when: z.unknown().optional(),
});

export const RemovalTriggerSchema = z
  .discriminatedUnion("kind", [
    TickDownSchema,
    RollGatedSchema,
    RestTriggeredSchema,
    ActionResolvedSchema,
    DamageTakenSchema,
    PendingActionResolvedSchema,
    SourceTurnStartedSchema,
  ])
  .superRefine((value, ctx) => {
    if (
      value.kind === "action-resolved" &&
      value.actionRef === undefined &&
      value.actionClasses === undefined &&
      value.forcesSave !== true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "action-resolved trigger requires at least one matcher: actionRef, actionClasses, or forcesSave",
      });
    }
  });

/**
 * Validate a single `RemovalTrigger<R>`. Throws on Zod shape mismatch or
 * on invalid `when?` predicate (LAF-163 `validatePredicateForSite`).
 *
 * Called at profile-register-time (via `profile-predicates/registry.ts`)
 * AND at apply-time (via `resolveRemovalTriggers`).
 */
export function validateRemovalTrigger<R extends string>(
  trigger: RemovalTrigger<R>
): void {
  const result = RemovalTriggerSchema.safeParse(trigger);
  if (!result.success) {
    throw new Error(`Invalid RemovalTrigger: ${result.error.message}`);
  }
  if (trigger.when !== undefined) {
    validatePredicateForSite(trigger.when, "removalTriggerWhen");
  }
}

/**
 * Validate an array of triggers. Used at register-time + apply-time.
 */
export function validateRemovalTriggers<R extends string>(
  triggers: readonly RemovalTrigger<R>[]
): void {
  for (const trigger of triggers) {
    validateRemovalTrigger(trigger);
  }
}
